import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Toolbar,
  TablePagination,
  Switch,
} from '@mui/material';
import { Close, Add, Edit, Delete, Search } from '@mui/icons-material';
import motivoDescontoService, { MotivoDesconto } from '../../services/motivoDescontoService';
import MotivoDescontoForm from '../forms/MotivoDescontoForm';

interface MotivoDescontoModalProps {
  open: boolean;
  onClose: () => void;
}

const MotivoDescontoModal: React.FC<MotivoDescontoModalProps> = ({ open, onClose }) => {
  const [motivos, setMotivos] = useState<MotivoDesconto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingMotivo, setEditingMotivo] = useState<MotivoDesconto | null>(null);

  const loadMotivos = async () => {
    try {
      setLoading(true);
      const response = await motivoDescontoService.getAllMotivos({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
      });
      setMotivos(response.motivos);
      setTotal(response.pagination.total);
    } catch (error) {
      console.error('Erro ao carregar motivos de desconto:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar motivos de desconto' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadMotivos();
    }
  }, [open, page, rowsPerPage, searchTerm]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreate = () => {
    setEditingMotivo(null);
    setFormOpen(true);
  };

  const handleEdit = (motivo: MotivoDesconto) => {
    setEditingMotivo(motivo);
    setFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este motivo de desconto?')) {
      try {
        await motivoDescontoService.deleteMotivo(id);
        setMessage({ type: 'success', text: 'Motivo de desconto excluído com sucesso!' });
        loadMotivos();
      } catch (error) {
        setMessage({ type: 'error', text: 'Erro ao excluir motivo de desconto' });
      }
    }
  };

  const handleToggleAtivo = async (id: number) => {
    try {
      await motivoDescontoService.toggleAtivo(id);
      setMessage({ type: 'success', text: 'Status alterado com sucesso!' });
      loadMotivos();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao alterar status' });
    }
  };

  const handleFormSuccess = () => {
    setMessage({ type: 'success', text: 'Motivo de desconto salvo com sucesso!' });
    setFormOpen(false);
    loadMotivos();
  };

  const handleFormError = (error: string) => {
    setMessage({ type: 'error', text: error });
  };

  const handleClose = () => {
    if (!loading) {
      setMotivos([]);
      setSearchTerm('');
      setPage(0);
      setMessage(null);
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Gerenciar Motivos de Desconto</Typography>
            <Box display="flex" gap={1}>
              <Button variant="contained" startIcon={<Add />} onClick={handleCreate} size="small">
                Cadastrar
              </Button>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          <Toolbar sx={{ pl: 0, pr: 0 }}>
            <TextField
              fullWidth
              placeholder="Buscar motivos..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Toolbar>

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : motivos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Nenhum motivo de desconto encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  motivos.map((motivo) => (
                    <TableRow key={motivo.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {motivo.descricao}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={motivo.ativo ? 'Ativo' : 'Inativo'}
                            color={motivo.ativo ? 'success' : 'default'}
                            size="small"
                          />
                          {motivo.is_default && (
                            <Chip
                              label="Padrão"
                              color="primary"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(motivo)}
                            color="primary"
                            disabled={!!motivo.is_default}
                            title={motivo.is_default ? "Motivo padrão não pode ser editado" : "Editar"}
                          >
                            <Edit />
                          </IconButton>
                          <Switch
                            checked={!!motivo.ativo}
                            onChange={() => handleToggleAtivo(motivo.id)}
                            color="primary"
                            disabled={!!motivo.is_default}
                            title={motivo.is_default ? "Motivo padrão não pode ser desativado" : "Alternar status"}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(motivo.id)}
                            color="error"
                            disabled={!!motivo.is_default}
                            title={motivo.is_default ? "Motivo padrão não pode ser excluído" : "Excluir"}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <MotivoDescontoForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        motivoDesconto={editingMotivo}
        onSuccess={handleFormSuccess}
        onError={handleFormError}
      />
    </>
  );
};

export default MotivoDescontoModal;
